import React, { useEffect, useState } from 'react'
import VehicleCard from './components/VehicleCard'
import Filters from './components/Filters'
import ResultsHeader from './components/ResultsHeader'
import useResponsiveView from '../../hooks/useResponsiveView'
import { CircularProgress, Drawer, Modal } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import StatusCard from './components/StatusCard'
import Pagination from './components/Pagination'
import hasCommonItem from '../../../js/helpers/hasCommonItem'
import getRangeBounds from '../../../js/helpers/getRangeBounds'
import {
  setMilesNBonusAccount,
  setFleetVehicle,
  setIsPrepaid,
  setSelectedVehicle,
  setStep,
  setSelectedExtras,
  setPaymentNotice,
  resetMilesNBonusAccount,
  resetMBData,
  resetSteps2And3,
} from '../../../js/lib/redux/slices/booking'
import { postData } from '../../../js/helpers/apiHelper'
import constants from '../../../js/lib/constants'
import VehiclesNoFound from './components/VehiclesNoFound'
import * as lib from '../../../js/lib/lib'
import StatusCardsWrapper from './components/StatusCardsWrapper'
import Alert from '../Base/Alert'

const BookingStep1 = ({ fleet, inpath, isMBLoggedIn }) => {
  const {
    records,
    dictionary,
    configuration,
    token,
    aegeanCard,
    mbCard,
    promoCard,
  } = window.fleetData || window.bookingData || window.inpathData
  const { milesNBonusAccount } = window.headerData || {}
  const [carsData, setCarsData] = useState(
    (fleet
      ? []
      : records?.cars?.sort((a, b) => a.sortGroupName - b.sortGroupName)) || []
  )
  const sortOptions = [
    {
      value: 'default',
      text: dictionary.recommended,
    },
    {
      value: 'priceAsc',
      text: dictionary.priceAsc,
    },
    {
      value: 'priceDesc',
      text: dictionary.priceDesc,
    },
  ]
  const pageSize = 10
  const dispatch = useDispatch()

  const {
    filters,
    sort,
    fleetVehicle,
    selectedExtras,
    inpathCars,
    latestSearch,
  } = useSelector((state) => state.booking)

  const [cars, setCars] = useState(carsData)
  const [gridView, setGridView] = useState(fleet)
  const [openFiltersDrawer, setOpenFiltersDrawer] = useState(false)
  const { isDevice, breakpoint } = useResponsiveView(1400)
  const [currentPage, setCurrentPage] = useState(1)
  const [openFleetVehicleModal, setOpenFleetVehicleModal] = useState(false)
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    fleet ? records.countries?.find((item) => item.selected)?.value : ''
  )
  const [loadingFleetData, setLoadingFleetData] = useState(true)
  const [fleetVehicleExistsLoader, setFleetVehicleExistsLoader] = useState(
    !fleet && !!fleetVehicle
  )

  const [isLoading, setIsLoading] = useState(false)
  const [alertMessages, setAlertMessages] = useState([])

  const currentCars = cars.filter(
    (_, index) =>
      index >= (currentPage - 1) * pageSize && index < currentPage * pageSize
  )

  const groups =
    currentCars?.length > 0
      ? Object.values(
          currentCars.reduce((acc, car) => {
            const { groupName } = car

            if (!acc[groupName]) acc[groupName] = { groupName, cars: [car] }
            else acc[groupName].cars.push(car)

            return acc
          }, {})
        )
      : []

  useEffect(() => {
    const handleLoginSuccess = (event) => {
      const isLoggedIn = event.detail.isLoggedIn

      if (!isLoggedIn) {
        dispatch(resetMilesNBonusAccount())
        dispatch(resetMBData())
      } else {
        dispatch(setMilesNBonusAccount(event.detail.data))
      }
      const currentUrl = window.location.href
      window.location.href = currentUrl.substring(
        0,
        currentUrl.lastIndexOf('/')
      )
    }
    window.addEventListener('loggedIn', handleLoginSuccess)

    return () => {
      window.removeEventListener('loggedIn', handleLoginSuccess)
    }
  }, [])

  const fetchFleetCars = () => {
    postData(configuration.endpointFetchData, {
      countryCode: selectedCountryCode,
    })
      .then((response) => {
        if (!response.hasError) {
          const sortedCars = response.data.cars.sort(
            (a, b) => a.sortGroupName - b.sortGroupName
          )
          setCarsData(sortedCars)
          setCars(sortedCars)
          setCurrentPage(1)
        }
      })
      .finally(() => {
        setLoadingFleetData(false)
      })
  }

  useEffect(() => {
    if (fleet) {
      fetchFleetCars()
    }
  }, [fleet, selectedCountryCode])

  useEffect(() => {
    if (
      !!milesNBonusAccount &&
      milesNBonusAccount?.fFN &&
      isMBLoggedIn?.current === undefined
    ) {
      dispatch(setMilesNBonusAccount(milesNBonusAccount))
    }

    if (
      !milesNBonusAccount?.fFN?.length > 0 &&
      isMBLoggedIn?.current === undefined
    ) {
      dispatch(resetMilesNBonusAccount())
    }
  }, [])

  const filterCars = (filters) => {
    const baseCars = inpath && inpathCars?.length ? inpathCars : carsData

    let newCars = [...baseCars]
    if (filters['brand']?.length > 0) {
      newCars = newCars.filter((car) =>
        filters['brand'].includes(car.brandCode)
      )
    }
    if (filters['category']?.length > 0) {
      newCars = newCars.filter((car) =>
        hasCommonItem(car.availableCategories, filters['category'])
      )
    }
    if (filters['type']?.length > 0) {
      newCars = newCars.filter((car) =>
        hasCommonItem(car.availableCategories, filters['type'])
      )
    }
    if (filters['passengers']?.length > 0) {
      const { min, max } = getRangeBounds(filters['passengers'])
      newCars = newCars.filter(
        (car) => car.passengers >= min && car.passengers <= max
      )
    }
    if (filters['luggage']?.length > 0) {
      const { min, max } = getRangeBounds(filters['luggage'])
      newCars = newCars.filter(
        (car) =>
          car.luggage + car.sLuggage >= min && car.luggage + car.sLuggage <= max
      )
    }
    return newCars
  }

  useEffect(() => {
    if (fleet && loadingFleetData) return
    const filteredCars = filterCars(filters)
    const filteredCarsWithPrice = filteredCars.map((car) => ({
      ...car,
      price: car.prepaidPrice?.total || car.nonPrepaidPrice?.total || 0,
    }))
    setCars(
      sort === 'priceAsc'
        ? filteredCarsWithPrice.sort((a, b) => a.price - b.price)
        : sort === 'priceDesc'
        ? filteredCarsWithPrice.sort((a, b) => b.price - a.price)
        : filteredCars
    )
    setCurrentPage(1)
  }, [filters, sort, loadingFleetData])

  const scrollToResults = () => {
    const resultsElement = document.querySelector('.s-results__cars')
    if (resultsElement) {
      const y = resultsElement.getBoundingClientRect().top + window.scrollY
      const offset = -110
      window.scrollTo({
        top: y + offset,
        behavior: 'smooth',
      })
    }
  }

  const handleBooking = async (vehicle, isPrepaid) => {
    if (fleet) {
      dispatch(
        setFleetVehicle({
          brandCode: vehicle.brandCode,
          groupName: vehicle.groupName,
          acriss: vehicle.acriss,
        })
      )
      setOpenFleetVehicleModal(true)
      return
    }

    if (isLoading) return
    setIsLoading(true)
    dispatch(resetSteps2And3())

    const {
      // values provided from DOM (InPath.htm)
      isGREngine,
      tarrifs,
      cdp,
      promoCode,
      goldNumber,
      MBLoginDataModel,
      currentValues,
    } = window.inpathData || {}

    // prefer latest criteria from Redux (set by InpathWidget) and
    // fall back to initial DOM currentValues when nothing is stored yet
    const criteria = latestSearch || currentValues || {}

    const dropOffDate = criteria?.dropOffDate
    const dropOffTime = criteria?.dropOffTime
    const pickUpDate = criteria?.pickUpDate
    const pickUpTime = criteria?.pickUpTime
    const dropOffLocation = criteria?.dropOffLocation
    const pickUpLocation = criteria?.pickUpLocation

    const payload = {
      amenities: [],
      vehicleGroup: vehicle.groupName,
      rateReference: vehicle.rateReference,
      brandCode: vehicle.brandCode,
      acriss: vehicle.acriss,
      isPrepaid,
      token,
      ...(inpath && window.inpathData
        ? {
            dropOffDate,
            dropOffLocation,
            dropOffTime,
            pickUpDate,
            pickUpLocation,
            pickUpTime,
            mbData: MBLoginDataModel,
            isGREngine: isGREngine ?? false,
            tarrifs,
            cdp,
            promoCode,
            goldNumber,
          }
        : {}),
    }

    if (!inpath) {
      const response = await postData(
        configuration.endpointGetSingleRate,
        payload
      )

      if (!response.success) {
        handleError(response.errorMessage ? response.errorMessage : undefined)
        return
      }

      lib.scrollToTop()
      dispatch(setSelectedVehicle(response.vehicle))
      dispatch(setPaymentNotice(response.paymentNotice))
      dispatch(setIsPrepaid(isPrepaid))
      dispatch(setStep(constants.booking.pageNumber.extras))
      dispatch(setSelectedExtras([]))
    } else {
      const inpathResponse = await postData(
        configuration.endpointInpathBookNow,
        payload
      )

      if (inpathResponse.hasError) {
        handleError(inpathResponse.errorMessages)
        return
      }

      window.AddCarToBasket(inpathResponse.data.inpathData)
      dispatch(setSelectedVehicle(inpathResponse.data.renderData))
      dispatch(setIsPrepaid(isPrepaid))
      dispatch(setStep(constants.booking.pageNumber.extras))
      dispatch(setSelectedExtras([]))
      lib.scrollToTop()
    }
    handleSuccess()
  }

  const handleSuccess = () => {
    setAlertMessages([])
    setIsLoading(false)
  }

  const handleError = (messages) => {
    typeof messages === 'string'
      ? setAlertMessages([messages])
      : setAlertMessages(messages)
    setIsLoading(false)
    if (!!fleetVehicle) setFleetVehicleExistsLoader(false)

    setTimeout(() => {
      lib.scrollToError(100)
    }, 0)
  }

  useEffect(() => {
    if (!fleet && !!fleetVehicle) {
      const selectedCar = cars.find((car) => {
        return (
          car.brandCode === fleetVehicle.brandCode &&
          car.acriss === fleetVehicle.acriss
        )
      })
      if (selectedCar) {
        setFleetVehicleExistsLoader(true)
        handleBooking(
          {
            ...selectedCar,
            groupName: fleetVehicle?.groupName,
          },
          selectedCar.prepaidPrice?.total > 0 ? true : false
        )
      } else {
        setAlertMessages([
          dictionary.fleetSelectedVehicleNotFound ||
            'The vehicle you selected is not available for selected location and/or selected dates. Please select another vehicle.',
        ])
        setFleetVehicleExistsLoader(false)
      }
    }
  }, [fleet, fleetVehicle])

  return (
    <div className="page-container">
      {alertMessages?.length > 0 && <Alert messages={alertMessages} />}

      {(fleetVehicleExistsLoader || (inpath && !inpathCars?.length)) && (
        <div className="s-results__loading">
          <CircularProgress />
        </div>
      )}
      {!fleetVehicleExistsLoader && !(inpath && !inpathCars?.length) && (
        <>
          <div className={`s-results ${inpath ? 's-results--inpath' : ''}`}>
            {breakpoint.up && !inpath && (
              <div className="s-results__filters">
                <Filters
                  filterCars={filterCars}
                  fleet={fleet}
                  carsData={carsData}
                  loadingFleetData={loadingFleetData}
                />
              </div>
            )}
            {fleet && loadingFleetData && (
              <div className="s-results__loading">
                <CircularProgress />
              </div>
            )}
            {!(fleet && loadingFleetData) && (
              <div className="s-results__main">
                {(fleet || cars?.length > 0) && (
                  <div className="s-results__header">
                    <ResultsHeader
                      totalCars={cars?.length || 0}
                      gridView={gridView}
                      handleGridView={() => setGridView(true)}
                      handleListView={() => setGridView(false)}
                      sortOptions={sortOptions}
                      hideViews={fleet || sort !== 'default'}
                      dictionary={dictionary}
                      fleet={fleet}
                      isInpath={inpath}
                      groups={groups}
                      handleChangeCountry={(value) =>
                        setSelectedCountryCode(value)
                      }
                      setOpenFiltersDrawer={setOpenFiltersDrawer}
                    />
                  </div>
                )}

                {!fleet && !inpath && cars?.length > 0 && (
                  <StatusCardsWrapper className="s-results__status-cards">
                    {!aegeanCard && !mbCard && !promoCard && (
                      <StatusCard
                        card={{
                          type: 'promo',
                          title: dictionary.bannerDefaultTitle,
                          description: dictionary.bannerDefaultText,
                          cta: dictionary.bannerAddBtnText,
                        }}
                        mbAccountData={milesNBonusAccount}
                        dictionary={dictionary}
                      />
                    )}
                    {aegeanCard && (
                      <StatusCard card={aegeanCard} dictionary={dictionary} />
                    )}
                    {mbCard && (
                      <StatusCard
                        card={mbCard}
                        mbAccountData={milesNBonusAccount}
                        dictionary={dictionary}
                      />
                    )}
                    {promoCard && (
                      <StatusCard card={promoCard} dictionary={dictionary} />
                    )}
                  </StatusCardsWrapper>
                )}
                <div
                  className={`s-results__cars ${
                    sort === 'default' && !fleet
                      ? 's-results__cars--groups'
                      : fleet
                      ? 's-results__cars--fleet'
                      : ''
                  }`}>
                  {sort === 'default' && !fleet
                    ? groups.map(({ groupName, cars }) => (
                        <div key={groupName} className="b-vehicle-group">
                          <h3 className="h3 b-vehicle-group__title">
                            {dictionary.group} {groupName}
                          </h3>
                          <div
                            className={`b-vehicle-group__cars ${
                              gridView && cars?.length === 2 && !isDevice
                                ? 'grid'
                                : ''
                            }`}>
                            {cars
                              .sort((a, b) => a.brandSort - b.brandSort)
                              .map((car, index) => (
                                <VehicleCard
                                  key={index}
                                  data={car}
                                  grid={
                                    (gridView && cars?.length === 2) || isDevice
                                  }
                                  isLoading={isLoading}
                                  handleBooking={(isPrepaid) =>
                                    handleBooking(car, isPrepaid)
                                  }
                                />
                              ))}
                          </div>
                        </div>
                      ))
                    : currentCars.map((car, index) => (
                        <VehicleCard
                          key={index}
                          data={car}
                          fleet={fleet}
                          grid={fleet}
                          isLoading={isLoading}
                          handleBooking={(isPrepaid) =>
                            handleBooking(car, isPrepaid)
                          }
                        />
                      ))}
                </div>

                {cars?.length > 0 && (
                  <div className="s-results__pagination">
                    <Pagination
                      page={currentPage}
                      totalRecords={cars?.length}
                      pageSize={pageSize}
                      onChange={(page) => {
                        setCurrentPage(page)
                        scrollToResults()
                      }}
                    />
                  </div>
                )}

                {cars?.length === 0 && !fleet && <VehiclesNoFound />}
              </div>
            )}
          </div>

          {breakpoint.down && !inpath && (
            <button
              type="button"
              className="btn btn-outline-primary s-results__floating-btn"
              onClick={() => setOpenFiltersDrawer(true)}
              title={dictionary.filtersFloatingBtn}>
              <i className="icon-filter"></i>
              {dictionary.filtersFloatingBtn}
            </button>
          )}

          {(breakpoint.down || inpath) && (
            <>
              <Drawer
                open={openFiltersDrawer}
                onClose={() => setOpenFiltersDrawer(false)}
                anchor="right"
                disableScrollLock={true}
                PaperProps={{
                  sx: {
                    maxWidth: '375px',
                  },
                }}>
                <Filters
                  filterCars={filterCars}
                  drawer
                  onCloseDrawer={() => setOpenFiltersDrawer(false)}
                  sortOptions={sortOptions}
                  fleet={fleet}
                  carsData={carsData}
                  isInpath={inpath}
                />
              </Drawer>
            </>
          )}
          {fleet && (
            <Modal
              open={openFleetVehicleModal}
              onClose={() => setOpenFleetVehicleModal(false)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              disableScrollLock={true}>
              <div className="b-fleet-modal">
                <div className="b-fleet-modal__header">
                  <i className="icon-check"></i>
                  <h3 className="m-0">{dictionary.fleetModalTitle}</h3>
                </div>
                <p className="m-0">
                  {dictionary.yourSelectionFor} "{dictionary.group}{' '}
                  {fleetVehicle?.groupName}" {dictionary.hasBeenSubmitted}
                </p>
                <button
                  type="button"
                  title="close"
                  className="btn p-0 b-fleet-modal__close"
                  onClick={() => setOpenFleetVehicleModal(false)}>
                  <i className="icon-x"></i>
                </button>
              </div>
            </Modal>
          )}
        </>
      )}
    </div>
  )
}

export default BookingStep1
